import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, MessageSquare, Loader, Send } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { ADK_API_BASE_URL, getSessionInfo, sendADKMessageStreaming, ADK_APP_NAME, getOAuthCredentials, withAuth, isAuthExpiredError, isPlanGoneError, planGoneError, readSSEStream } from '../config/api';
import { PlatformType } from '../types';
import { isTriggerCommand } from '../lib/triggers';
import AiLabel, { isAppErrorJson } from './AiLabel';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
}

interface BriefUploadStepProps {
  sessionId: string;
  platform: PlatformType;
  onBriefRefined: () => void;
  onLogout: () => void;
}

const BriefUploadStep: React.FC<BriefUploadStepProps> = ({
  sessionId,
  platform,
  onBriefRefined,
  onLogout
}) => {
  const [briefFile, setBriefFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refined, setRefined] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showOutput, setShowOutput] = useState(false);
  const [inputMode, setInputMode] = useState<'file' | 'gdoc'>('file');
  const [gdocUrl, setGdocUrl] = useState('');
  const [gdocError, setGdocError] = useState('');
  const [fetchingDoc, setFetchingDoc] = useState(false);

  const outputRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [messages]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setBriefFile(file);
    }
  };

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || !sessionId) return;

    const { userId } = getSessionInfo();

    // The panel sends `RUN_BRIEF_REFINEMENT\nSession ID: …` to start the step.
    // Echoing that put the command in the transcript as though the user had
    // typed it, taking a full row before the agent had said anything.
    if (!isTriggerCommand(messageText)) {
      const userMessage: Message = {
        id: Date.now().toString(),
        type: 'user',
        content: messageText,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);
    }
    setInputValue('');
    setRefining(true);

    try {
      // Follow-ups MUST carry the RUN_BRIEF_REFINEMENT command. Without it the
      // backend router falls through to the root orchestrator, which holds
      // every step's tools and will happily run a later step: a stray word
      // typed here once wrote define_objectives_output.json and streamed an
      // objectives report into this panel, while the stepper still read
      // "Brief" because nothing had actually advanced.
      const messageWithSessionId = messageText.startsWith('RUN_BRIEF_REFINEMENT')
        ? messageText // Initial message already has session ID
        : `RUN_BRIEF_REFINEMENT\nSession ID: ${sessionId}\nUser Message: ${messageText}`;

      // Stream the agent response
      const stream = await sendADKMessageStreaming(ADK_APP_NAME, userId, sessionId, messageWithSessionId, platform);

      let assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true
      };
      setMessages(prev => [...prev, assistantMessage]);

      let fullContent = '';
      let expired = false;

      // readSSEStream buffers across reads. Splitting each read on its own
      // silently dropped any frame the network happened to cut in half, and
      // the bigger the frame the likelier that was.
      await readSSEStream(stream, data => {
        if (data.type === 'auth_expired') {
          expired = true;
        } else if (data.type === 'content' && data.text) {
          fullContent += data.text;
          setMessages(prev =>
            prev.map(msg =>
              msg.id === assistantMessage.id
                ? { ...msg, content: fullContent }
                : msg
            )
          );
        }
      });

      if (expired) {
        onLogout();
        return;
      }

      // Check if brief is approved AND saved (must include "saved" to ensure finalize tool was called)
      if (fullContent.includes('Campaign brief approved and saved')) {
        setRefined(true);
      }

      // Mark as not streaming
      setMessages(prev =>
        prev.map(msg =>
          msg.id === assistantMessage.id
            ? { ...msg, isStreaming: false }
            : msg
        )
      );

      setRefining(false);
    } catch (error) {
      if (isAuthExpiredError(error)) {
        // Same pre-stream 401 as AgentWorkspace: the auth dependency rejects
        // before any SSE body exists, so the `auth_expired` event below never
        // arrives and only this path can recover.
        console.warn('Session expired; signing out to re-authenticate.');
        onLogout();
        return;
      }
      console.error('Error sending message:', error);
      if (isPlanGoneError(error)) alert(error.message);
      setRefining(false);
    }
  };

  const handleUploadAndRefine = async () => {
    if (!briefFile || !sessionId) return;

    setUploading(true);
    const { userId } = getSessionInfo();

    try {
      // Upload the brief file
      const formData = new FormData();
      formData.append('file', briefFile);
      formData.append('session_id', sessionId);
      formData.append('user_id', userId);

      // withAuth adds only Authorization, leaving Content-Type unset so the
      // browser sets the multipart boundary itself.
      const response = await fetch(`${ADK_API_BASE_URL}/upload-brief`, await withAuth({
        method: 'POST',
        body: formData,
      }));

      if (response.status === 410) {
        throw await planGoneError(response);
      }
      if (!response.ok) {
        throw new Error('Failed to upload brief');
      }

      console.log('Brief uploaded successfully');
      setUploading(false);
      setShowOutput(true);

      // Trigger brief refinement
      const initialMessage = `RUN_BRIEF_REFINEMENT\nSession ID: ${sessionId}`;
      await sendMessage(initialMessage);

    } catch (error) {
      console.error('Error uploading brief:', error);
      // A gone plan cannot be fixed by retrying: say so, in the backend's words.
      alert(isPlanGoneError(error) ? error.message : 'Failed to upload brief. Please try again.');
      setUploading(false);
      setRefining(false);
    }
  };

  const handleFetchGdoc = async () => {
    if (!gdocUrl.trim() || !sessionId) return;

    setFetchingDoc(true);
    setGdocError('');
    const { userId } = getSessionInfo();

    const creds = getOAuthCredentials();
    if (!creds?.access_token) {
      onLogout();
      return;
    }
    const accessToken = creds.access_token;

    try {
      const response = await fetch(`${ADK_API_BASE_URL}/fetch-brief-gdoc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ url: gdocUrl, session_id: sessionId, user_id: userId }),
      });

      if (response.status === 401) {
        onLogout();
        return;
      }

      if (response.status === 410) {
        // The plan is past its retention limit; a retry can never work.
        alert((await planGoneError(response)).message);
        return;
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({ detail: 'Failed to fetch document' }));
        setGdocError(err.detail || 'Failed to fetch document');
        return;
      }

      setShowOutput(true);
      const initialMessage = `RUN_BRIEF_REFINEMENT\nSession ID: ${sessionId}`;
      await sendMessage(initialMessage);
    } catch (_) {
      setGdocError('Network error. Please try again.');
    } finally {
      setFetchingDoc(false);
    }
  };

  const handleSendInput = () => {
    if (inputValue.trim() && !refining) {
      sendMessage(inputValue);
    }
  };

  const handleProceed = () => {
    onBriefRefined();
  };

  return (
    <>
      {/* Show file upload UI */}
      {!showOutput && (
        <div className="config-content">
          <motion.div
            className="config-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="config-title">Step 2: Upload Brief</h2>
            <p className="config-subtitle">Upload your campaign brief for AI-powered refinement</p>

            <div className="config-form">
              {/* Tab switcher */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={() => { setInputMode('file'); setGdocError(''); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '2px solid',
                    borderColor: inputMode === 'file' ? '#8b5cf6' : '#e5e7eb',
                    borderRadius: '8px',
                    background: inputMode === 'file' ? '#f5f3ff' : 'white',
                    color: inputMode === 'file' ? '#7c3aed' : '#6b7280',
                    fontWeight: inputMode === 'file' ? 600 : 400,
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Upload File
                </button>
                <button
                  type="button"
                  onClick={() => { setInputMode('gdoc'); setGdocError(''); }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    border: '2px solid',
                    borderColor: inputMode === 'gdoc' ? '#8b5cf6' : '#e5e7eb',
                    borderRadius: '8px',
                    background: inputMode === 'gdoc' ? '#f5f3ff' : 'white',
                    color: inputMode === 'gdoc' ? '#7c3aed' : '#6b7280',
                    fontWeight: inputMode === 'gdoc' ? 600 : 400,
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  Google Doc / Sheet
                </button>
              </div>

              {inputMode === 'file' && (
                <div className="form-group">
                  <label>Campaign Brief</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="brief-file"
                      accept=".pdf,.doc,.docx,.txt,.md"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      disabled={uploading || refining || refined}
                    />
                    <label htmlFor="brief-file" className="file-upload-label">
                      {briefFile ? (
                        <div className="file-selected">
                          <Check size={20} />
                          <span>{briefFile.name}</span>
                        </div>
                      ) : (
                        <div className="file-placeholder">
                          <MessageSquare size={32} />
                          <span>Click to upload brief</span>
                          <span className="file-hint">PDF, DOC, DOCX, TXT, or MD files accepted</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {uploading && (
                    <div className="loading-indicator">
                      <Loader className="spinner" size={16} />
                      <span>Uploading brief...</span>
                    </div>
                  )}

                  <button
                    className="continue-button"
                    onClick={handleUploadAndRefine}
                    disabled={!briefFile || uploading}
                  >
                    {uploading ? 'Uploading...' : 'Upload and Refine Brief'}
                  </button>
                </div>
              )}

              {inputMode === 'gdoc' && (
                <div className="form-group">
                  <label>Google Doc or Sheet URL</label>
                  <input
                    type="url"
                    className="chat-input"
                    placeholder="Paste Google Doc or Sheet URL..."
                    value={gdocUrl}
                    onChange={(e) => { setGdocUrl(e.target.value); setGdocError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && gdocUrl.startsWith('https://docs.google.com/') && !fetchingDoc) handleFetchGdoc(); }}
                    disabled={fetchingDoc}
                    style={{ width: '100%', marginBottom: '8px', boxSizing: 'border-box' }}
                  />
                  {gdocError && (
                    <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 8px 0' }}>
                      {gdocError}
                    </p>
                  )}
                  <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px 0' }}>
                    For Google Sheets: navigate to the tab you want to use, then copy the URL.
                  </p>
                  {fetchingDoc && (
                    <div className="loading-indicator">
                      <Loader className="spinner" size={16} />
                      <span>Fetching document...</span>
                    </div>
                  )}
                  <button
                    className="continue-button"
                    onClick={handleFetchGdoc}
                    disabled={!gdocUrl.startsWith('https://docs.google.com/') || fetchingDoc}
                  >
                    {fetchingDoc ? 'Fetching...' : 'Fetch & Refine Brief'}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Show full-screen chat interface */}
      {showOutput && (
        <div className="brief-chat-fullscreen">
          <div className="brief-chat-container">
            <div className="brief-chat-header">
              <MessageSquare size={18} />
              <span>AI Brief Refinement</span>
              {refining && <Loader className="spinner" size={16} />}
            </div>
            <div className="brief-chat-messages" ref={outputRef}>
              <AnimatePresence>
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    className={`message ${message.type}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="message-content">
                      {/* The brief agent is Gemini: its answers are AI output. */}
                      {message.type === 'assistant' && message.content.trim() !== '' && !isAppErrorJson(message.content) && <AiLabel />}
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm, remarkBreaks]}
                        className="markdown-content"
                      >
                        {message.content}
                      </ReactMarkdown>
                      {message.isStreaming && <span className="cursor">▊</span>}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {refined && (
              <div className="success-message">
                <Check size={20} />
                <span>Brief refined successfully! You can now choose your audiences.</span>
              </div>
            )}

            {!refined && (
              <div className="brief-chat-input-container">
                <input
                  ref={inputRef}
                  type="text"
                  className="chat-input"
                  placeholder="Type your response..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendInput()}
                  disabled={refining}
                />
                <button
                  className="send-button"
                  onClick={handleSendInput}
                  disabled={refining || !inputValue.trim()}
                >
                  <Send size={18} />
                </button>
              </div>
            )}

            {refined && (
              <div className="brief-chat-footer">
                <button
                  className="continue-button"
                  onClick={handleProceed}
                >
                  Proceed to Audiences
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BriefUploadStep;
